import { useEffect, useState, useCallback } from "react";
import { applyUpdate } from "@/lib/pwa-updater";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { PwaUpdateModal } from "./PwaUpdateModal";

const DISMISS_KEY = "gerio_pwa_update_dismissed_at";
const DISMISS_HOURS = 12;

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const diff = Date.now() - Number(raw);
  return diff < DISMISS_HOURS * 60 * 60 * 1000;
}

/** Globally accessible flag so Settings page can check */
let _updateAvailable = false;
export function isUpdateAvailable() { return _updateAvailable; }

export function PwaUpdateToast() {
  const [visible, setVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const handler = () => {
      _updateAvailable = true;
      if (!wasDismissedRecently()) {
        setVisible(true);
      }
    };
    window.addEventListener("pwa-update-available", handler);
    return () => window.removeEventListener("pwa-update-available", handler);
  }, []);

  // Also expose a way for settings to trigger the update
  useEffect(() => {
    const handler = () => {
      setVisible(false);
      setUpdating(true);
    };
    window.addEventListener("pwa-trigger-update", handler);
    return () => window.removeEventListener("pwa-trigger-update", handler);
  }, []);

  useEffect(() => {
    if (!updating) return;
    const handler = () => applyUpdate();
    window.addEventListener("pwa-do-update", handler);
    return () => window.removeEventListener("pwa-do-update", handler);
  }, [updating]);

  const handleUpdate = () => {
    setVisible(false);
    setUpdating(true);
  };

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  return (
    <>
      {/* Mobile: centered modal overlay */}
      {visible && !updating && isMobile && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="mx-6 w-full max-w-sm rounded-2xl bg-background p-6 shadow-2xl border border-border/40 flex flex-col items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
              <RefreshCw className="h-7 w-7 text-primary" />
            </div>
            <div className="text-center space-y-1.5">
              <h3 className="text-lg font-bold text-foreground">Nova versão disponível</h3>
              <p className="text-sm text-muted-foreground">Atualiza a app para teres acesso às últimas melhorias.</p>
            </div>
            <Button onClick={handleUpdate} className="w-full h-11 rounded-xl font-semibold">
              Atualizar agora
            </Button>
            <button
              onClick={handleDismiss}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Mais tarde
            </button>
          </div>
        </div>
      )}

      {/* Web: bottom-right toast banner */}
      {visible && !updating && !isMobile && (
        <div className="fixed top-auto left-auto right-6 bottom-6 max-w-sm z-[210] animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 shadow-lg">
            <RefreshCw className="h-5 w-5 text-primary shrink-0" />
            <p className="text-sm font-medium flex-1">Nova versão disponível</p>
            <Button size="sm" onClick={handleUpdate}>
              Atualizar agora
            </Button>
          </div>
        </div>
      )}

      <PwaUpdateModal open={updating} />
    </>
  );
}
